/**
 * Undoable edit operations over a SplatEditModel.
 *
 * State changes are recorded as XOR runs (applying the same XOR undoes it), and
 * palette edits as the run of affected splats plus an old→new index map, so a
 * "select all" on 20M splats costs a few bytes of history.
 */
import { Ranges } from './Ranges';
import { SplatState, UNSELECTABLE, applySelectionBit, type SelectionOp } from './SplatState';
import type { SplatEditModel } from './SplatEditModel';
import { composeGrades, createGradeTerms, type GradeTerms } from './ColorGrade';

export interface EditOp {
    readonly name: string;
    do(): void;
    undo(): void;
    /** Release resources when dropped from history. */
    destroy?(): void;
}

// ─── State (selected / hidden / deleted) ───────────────────────

/** XOR delta over state bytes, grouped by XOR value. */
export class StateDelta {
    readonly groups: { xor: number; ranges: Ranges }[];

    private constructor(groups: { xor: number; ranges: Ranges }[]) {
        this.groups = groups;
    }

    /** Build from a function giving each splat's new state byte. */
    static compute(model: SplatEditModel, next: (state: number, i: number) => number): StateDelta {
        const n = model.numSplats;
        const state = model.state;
        const xors = new Uint8Array(n);
        const seen = new Set<number>();
        for (let i = 0; i < n; i++) {
            const s = state[i];
            const x = (next(s, i) ^ s) & 0xff;
            xors[i] = x;
            if (x) seen.add(x);
        }
        const groups = [...seen].map((xor) => ({
            xor,
            ranges: Ranges.fromPredicate(n, (i) => xors[i] === xor),
        }));
        return new StateDelta(groups);
    }

    get changed(): number {
        let c = 0;
        for (const g of this.groups) c += g.ranges.count;
        return c;
    }

    get empty(): boolean {
        return this.groups.length === 0;
    }

    apply(model: SplatEditModel): void {
        const state = model.state;
        for (const { xor, ranges } of this.groups) {
            const d = ranges.data;
            for (let r = 0; r < d.length; r += 2) {
                for (let i = d[r]; i < d[r + 1]; i++) state[i] ^= xor;
            }
        }
        model.markStateChanged(this.groups.some((g) => g.xor & (SplatState.hidden | SplatState.deleted)));
    }
}

export class StateOp implements EditOp {
    readonly name: string;
    readonly delta: StateDelta;
    private readonly model: SplatEditModel;

    constructor(model: SplatEditModel, name: string, next: (state: number, i: number) => number) {
        this.model = model;
        this.name = name;
        this.delta = StateDelta.compute(model, next);
    }

    get empty(): boolean {
        return this.delta.empty;
    }

    do(): void { this.delta.apply(this.model); }
    undo(): void { this.delta.apply(this.model); }
}

export const selectOp = (model: SplatEditModel, mask: ArrayLike<number>, op: SelectionOp, name = 'Select') =>
    // a mask built before rows were appended is shorter than the model: missing entries are misses
    new StateOp(model, name, (s, i) => applySelectionBit(s, !!mask[i], op));

export const selectAllOp = (model: SplatEditModel) =>
    new StateOp(model, 'Select All', (s) => (s & UNSELECTABLE ? s : s | SplatState.selected));

export const selectNoneOp = (model: SplatEditModel) =>
    new StateOp(model, 'Select None', (s) => s & ~SplatState.selected);

export const selectInvertOp = (model: SplatEditModel) =>
    new StateOp(model, 'Invert Selection', (s) => (s & UNSELECTABLE ? s : s ^ SplatState.selected));

/** Hide ("lock") the selection: hidden splats stay in the file but can't be selected. */
export const hideSelectionOp = (model: SplatEditModel) =>
    new StateOp(model, 'Hide Selection', (s) => (s & SplatState.selected ? (s | SplatState.hidden) & ~SplatState.selected : s));

export const unhideAllOp = (model: SplatEditModel) =>
    new StateOp(model, 'Unhide All', (s) => (s & SplatState.deleted ? s : s & ~SplatState.hidden));

export const deleteSelectionOp = (model: SplatEditModel) =>
    new StateOp(model, 'Delete Selection', (s) => (s & SplatState.selected ? (s | SplatState.deleted) & ~SplatState.selected : s));

/** Bring back every deleted splat (SuperSplat "Reset Splat"). */
export const restoreDeletedOp = (model: SplatEditModel) =>
    new StateOp(model, 'Restore Deleted', (s) => s & ~SplatState.deleted);

/** Show only the selection: hide every selectable splat that isn't selected. */
export const isolateSelectionOp = (model: SplatEditModel) =>
    new StateOp(model, 'Isolate Selection', (s) => (s & UNSELECTABLE || s & SplatState.selected ? s : s | SplatState.hidden));

// ─── Palette edits (transform / colour) ───────────────────────

/** Splats a palette edit applies to: the selection, or every live splat when nothing is selected. */
export function editTargets(model: SplatEditModel, selectionOnly = false): Ranges {
    const state = model.state;
    let anySelected = false;
    for (let i = 0; i < model.numSplats; i++) {
        if ((state[i] & (SplatState.selected | SplatState.deleted)) === SplatState.selected) { anySelected = true; break; }
    }
    if (anySelected || selectionOnly) {
        return Ranges.fromPredicate(model.numSplats, (i) => (state[i] & (SplatState.selected | SplatState.deleted)) === SplatState.selected);
    }
    return Ranges.fromPredicate(model.numSplats, (i) => (state[i] & SplatState.deleted) === 0);
}

/**
 * Move/rotate/scale a set of splats by a model-space matrix (column-major).
 * Allocates one new palette entry per distinct existing transform index.
 */
export class TransformSplatsOp implements EditOp {
    readonly name = 'Transform Splats';
    private readonly model: SplatEditModel;
    private readonly targets: Ranges;
    private readonly matrix: Float32Array;
    private forward = new Map<number, number>();
    private backward = new Map<number, number>();
    private allocated = 0;

    constructor(model: SplatEditModel, targets: Ranges, modelSpaceMatrix: ArrayLike<number>) {
        this.model = model;
        this.targets = targets;
        this.matrix = Float32Array.from(modelSpaceMatrix);
    }

    get empty(): boolean {
        return this.targets.empty;
    }

    do(): void {
        const model = this.model;
        const pal = model.transformPalette;
        const ti = model.transformIndex;
        const sources = new Set<number>();
        this.targets.forEach((i) => sources.add(ti[i]));
        const base = pal.alloc(sources.size);
        this.allocated = sources.size;
        this.forward.clear();
        this.backward.clear();
        const existing = new Float32Array(16);
        const combined = new Float32Array(16);
        let k = 0;
        for (const src of sources) {
            const dst = base + k++;
            pal.getMatrix(src, existing);
            mul4(this.matrix, existing, combined);
            pal.setMatrix(dst, combined);
            this.forward.set(src, dst);
            this.backward.set(dst, src);
        }
        this.targets.forEach((i) => { ti[i] = this.forward.get(ti[i])!; });
        model.markTransformChanged();
    }

    undo(): void {
        const ti = this.model.transformIndex;
        this.targets.forEach((i) => { ti[i] = this.backward.get(ti[i]) ?? ti[i]; });
        this.model.transformPalette.free(this.allocated);
        this.model.markTransformChanged();
    }
}

/**
 * Apply (compose) a colour grade to a set of splats, or reset them to no
 * grade when `terms` is null.
 */
export class ColorGradeOp implements EditOp {
    readonly name: string;
    private readonly model: SplatEditModel;
    private readonly targets: Ranges;
    private readonly terms: GradeTerms | null;
    private forward = new Map<number, number>();
    private allocated = 0;
    private previous: Uint32Array | null = null;

    constructor(model: SplatEditModel, targets: Ranges, terms: GradeTerms | null) {
        this.model = model;
        this.targets = targets;
        this.terms = terms ? { m: terms.m.slice(), offset: { ...terms.offset }, transparency: terms.transparency } : null;
        this.name = terms ? 'Apply Colour' : 'Reset Colour';
    }

    get empty(): boolean {
        return this.targets.empty;
    }

    do(): void {
        const model = this.model;
        const ci = model.colorIndex;
        if (!this.terms) {
            // reset: remember previous indices for undo (only the targets)
            this.previous = new Uint32Array(this.targets.count);
            let o = 0;
            this.targets.forEach((i) => { this.previous![o++] = ci[i]; ci[i] = 0; });
            model.markColorChanged();
            return;
        }
        const pal = model.colorPalette;
        const sources = new Set<number>();
        this.targets.forEach((i) => sources.add(ci[i]));
        const base = pal.alloc(sources.size);
        this.allocated = sources.size;
        this.forward.clear();
        const existing = createGradeTerms();
        const out = createGradeTerms();
        let k = 0;
        for (const src of sources) {
            const dst = base + k++;
            pal.getTerms(src, existing);
            composeGrades(existing, this.terms, out);
            pal.setTerms(dst, out);
            this.forward.set(src, dst);
        }
        this.previous = new Uint32Array(this.targets.count);
        let o = 0;
        this.targets.forEach((i) => { this.previous![o++] = ci[i]; ci[i] = this.forward.get(ci[i])!; });
        model.markColorChanged();
    }

    undo(): void {
        const model = this.model;
        const ci = model.colorIndex;
        if (this.previous) {
            let o = 0;
            this.targets.forEach((i) => { ci[i] = this.previous![o++]; });
        }
        if (this.allocated) model.colorPalette.free(this.allocated);
        this.allocated = 0;
        model.markColorChanged();
    }
}

/** Change the whole-splat data transform (G in world = parent·L·G·P·x). */
export class DataTransformOp implements EditOp {
    readonly name: string;
    private readonly apply: (m: Float32Array) => void;
    private readonly before: Float32Array;
    private readonly after: Float32Array;

    constructor(name: string, before: ArrayLike<number>, after: ArrayLike<number>, apply: (m: Float32Array) => void) {
        this.name = name;
        this.before = Float32Array.from(before);
        this.after = Float32Array.from(after);
        this.apply = apply;
    }

    do(): void { this.apply(this.after); }
    undo(): void { this.apply(this.before); }
}

/**
 * Duplicate the selected splats: copies are appended, become the selection,
 * and the originals are deselected so a following move drags the copies.
 */
export class DuplicateOp implements EditOp {
    readonly name = 'Duplicate';
    private readonly model: SplatEditModel;
    private readonly rows: Uint32Array;
    private readonly deselect: StateDelta;
    private start = -1;

    constructor(model: SplatEditModel) {
        this.model = model;
        const state = model.state;
        const rows: number[] = [];
        for (let i = 0; i < model.numSplats; i++) {
            if ((state[i] & (SplatState.selected | SplatState.deleted)) === SplatState.selected) rows.push(i);
        }
        this.rows = Uint32Array.from(rows);
        this.deselect = StateDelta.compute(model, (s) => s & ~SplatState.selected);
    }

    get empty(): boolean {
        return this.rows.length === 0;
    }

    do(): void {
        this.deselect.apply(this.model);
        this.start = this.model.appendRows(this.rows, SplatState.selected);
    }

    undo(): void {
        this.model.truncateRows(this.start);
        this.deselect.apply(this.model);
    }
}

/**
 * One clone stamp stroke: append copies of `rows` moved by a model-space
 * matrix (through the transform palette, so earlier moves and SH rotation stay
 * exact) and, in replace mode, delete the live splats the stroke painted over.
 * One undo step.
 */
export class CloneStampOp implements EditOp {
    readonly name = 'Clone Stamp';
    private readonly model: SplatEditModel;
    private readonly rows: Uint32Array;
    private readonly matrix: Float32Array;
    private readonly cover: StateDelta | null;
    private move: TransformSplatsOp | null = null;
    private start = -1;

    constructor(model: SplatEditModel, rows: ArrayLike<number>, modelSpaceMatrix: ArrayLike<number>, covered: ArrayLike<number> = []) {
        this.model = model;
        // only rows that exist (a stale stroke must never copy missing rows as NaN splats)
        this.rows = Uint32Array.from(Array.from(rows).filter((r) => r >= 0 && r < model.numSplats)).sort();
        this.matrix = Float32Array.from(modelSpaceMatrix);
        if (covered.length) {
            const hit = new Uint8Array(model.numSplats);
            for (let k = 0; k < covered.length; k++) hit[covered[k]] = 1;
            const delta = StateDelta.compute(model, (s, i) => (hit[i] && !(s & UNSELECTABLE)
                ? (s | SplatState.deleted) & ~SplatState.selected : s));
            this.cover = delta.empty ? null : delta;
        } else {
            this.cover = null;
        }
    }

    get empty(): boolean {
        return this.rows.length === 0 && !this.cover;
    }

    /** Number of splats added. */
    get added(): number {
        return this.rows.length;
    }

    /** Number of splats painted over (deleted). */
    get replaced(): number {
        return this.cover?.changed ?? 0;
    }

    do(): void {
        const model = this.model;
        this.cover?.apply(model);
        if (!this.rows.length) return;
        this.start = model.appendRows(this.rows, 0);
        const copies = new Uint32Array(this.rows.length);
        for (let j = 0; j < copies.length; j++) copies[j] = this.start + j;
        this.move = new TransformSplatsOp(model, Ranges.fromSortedIndices(copies), this.matrix);
        this.move.do();
    }

    undo(): void {
        const model = this.model;
        if (this.move) {
            this.move.undo();
            this.move = null;
            model.truncateRows(this.start);
        }
        this.cover?.apply(model);
    }
}

export class MultiOp implements EditOp {
    readonly name: string;
    readonly ops: EditOp[];

    constructor(name: string, ops: EditOp[]) {
        this.name = name;
        this.ops = ops;
    }

    do(): void { for (const op of this.ops) op.do(); }
    undo(): void { for (let i = this.ops.length - 1; i >= 0; i--) this.ops[i].undo(); }
    destroy(): void { for (const op of this.ops) op.destroy?.(); }
}

/** out = a * b for column-major 4x4 matrices. */
export function mul4(a: ArrayLike<number>, b: ArrayLike<number>, out: Float32Array): Float32Array {
    const r = new Float32Array(16);
    for (let c = 0; c < 4; c++) {
        for (let row = 0; row < 4; row++) {
            r[c * 4 + row] = a[row] * b[c * 4] + a[4 + row] * b[c * 4 + 1] + a[8 + row] * b[c * 4 + 2] + a[12 + row] * b[c * 4 + 3];
        }
    }
    out.set(r);
    return out;
}
